import {inject, Injectable} from '@angular/core';
import {BaseService} from 'src/app/services/base.service';
import {catchError, map, shareReplay, startWith, take} from 'rxjs/operators';
import {BehaviorSubject, Observable, of, switchMap} from 'rxjs';
import {BadgeRepo} from "./badge-repo";
import {BadgePageable} from "src/app/models/badge/badge-pageable";
import {BadgeSearchFilter} from "src/app/models/badge/badge";
import {BadgeRequestRepo} from "../../badge-request/repo/badge-request-repo";
import {DEFAULT_OFFSET} from "@gnv-utils";
import {BadgeRequestPageable} from "src/app/models/badge-request/badge-request-pageable";
import {BadgeRequestState} from "src/app/models/badge-request/badge-request-state";
import {RouteIndicatorItem} from "src/app/shared/types/routes/indicator-route.types";
import {TranslateService} from "@ngx-translate/core";
import {
  DEFAULT_LOADING_VALUE,
  DEFAULT_MIN_VALUE,
  formatTabIndicatorValue
} from 'src/app/shared/types/tab-indicator/tab-indicator';

const DEFAULT_MIN_PAGE_SIZE = 15;

@Injectable({
    providedIn: 'root',
})
export class BadgeCountRepo extends BaseService {

    private readonly badgeRepo = inject(BadgeRepo);
    private readonly badgeRequestRepo = inject(BadgeRequestRepo);
    private readonly translate = inject(TranslateService);

    private readonly baseQuery = {
        offset: DEFAULT_OFFSET,
        limit: DEFAULT_MIN_PAGE_SIZE,
    };

    private startIndicator: RouteIndicatorItem = {
        value: DEFAULT_LOADING_VALUE,
        tooltip: this.translate.instant('tabs-indicators.common.loading-tooltip')
    };

    private refreshStock$ = new BehaviorSubject<void>(undefined);
    private refreshAbnormalities$ = new BehaviorSubject<void>(undefined);
    private refreshRequests$ = new BehaviorSubject<void>(undefined);

    // ========================================

    public stockCount$ = this.refreshStock$.pipe(
        startWith(undefined),
        switchMap(() => this.loadStockCount()),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    private loadStockCount(): Observable<RouteIndicatorItem> {
        const query: BadgeSearchFilter = {
            ...this.baseQuery,
            hasTransactionsAuthSupport: false,
            availableAuthSupport: true
        };

        return this.badgeRepo.getBadgeStock(query).pipe(
            take(1),
            map((res: BadgePageable) => {
                const count = res?.pageable?.totalSize ?? 0;

                return ({
                    value: formatTabIndicatorValue(count),
                    tooltip: this.translate.instant('tabs-indicators.badges.stock.tooltip', { count }),
                });
            }),
            startWith(this.startIndicator),
            catchError(() => of({ value: DEFAULT_MIN_VALUE }))
        );
    }

    // ========================================

    public abnormalitiesCount$ = this.refreshAbnormalities$.pipe(
        startWith(undefined),
        switchMap(() => this.loadAbnormalitiesCount()),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    private loadAbnormalitiesCount(): Observable<RouteIndicatorItem> {
        const query: BadgeSearchFilter = {
            ...this.baseQuery,
            hasTransactionsAuthSupport: true,
            availableAuthSupport: true,
            hasSynchroFailed: true
        };

        return this.badgeRepo.getBadgeAbnormalities(query).pipe(
            map((res: BadgePageable) => {
                const count = res?.pageable?.totalSize ?? 0;

                return ({
                    value: formatTabIndicatorValue(count),
                    tooltip: this.translate.instant('tabs-indicators.badges.abnormalities.tooltip', { count }),
                });
            }),
            startWith(this.startIndicator),
            catchError(() => of({ value: DEFAULT_MIN_VALUE }))
        );
    }

    public refreshAbnormalities(): void {
        this.refreshAbnormalities$.next();
    }

    // ========================================

    public requestsCount$ = this.refreshRequests$.pipe(
        startWith(undefined),
        switchMap(() => this.loadRequestsCount()),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    private loadRequestsCount(): Observable<RouteIndicatorItem> {
        return this.badgeRequestRepo.getList(
            this.baseQuery.offset,
            this.baseQuery.limit,
            [BadgeRequestState.OPENED]
        ).pipe(
            take(1),
            map((res: BadgeRequestPageable) => {
                const count = res?.pageable?.totalSize ?? 0;

                return ({
                    value: formatTabIndicatorValue(count),
                    tooltip: this.translate.instant('tabs-indicators.badges.requests.tooltip', { count }),
                });
            }),
            startWith(this.startIndicator),
            catchError(() => of({ value: DEFAULT_MIN_VALUE }))
        );
    }

    public refreshRequests(): void {
        this.refreshRequests$.next();
    }
}
